import { FC, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Checkbox, Stack, Typography, Box, Divider } from '@mui/material';
import { doc, getDoc } from 'firebase/firestore';
import theme from '@/theme';
import { doCreateUserWithEmailAndPassword, doSignInWithGoogle } from '@/firebase/auth';
import { db } from '@/firebase/firebase';
import { RouteMap } from '@/routes/Routes';
import useAlertStore from '@/stores/alertStore';
import { errorReporter } from '@/services/errorReportingService';
import { FirebaseError } from 'firebase/app';

import { FormBox, FormContent, PolicyText, Title, TabContainer, TabButton, GoogleButton } from './styles';

import { LargeTextfield } from '@/components/CustomTextfield/styles';
import PasswordField from './PasswordField';
import PhoneNumberField from './PhoneNumberField';
import { getSignUpSchema, SignUpSchema } from './schema';
import { InfoOutlined } from '@mui/icons-material';
import { GoogleIcon } from '@/components/Icons/Icons';
import { clearLogoutFlag } from '@/utils/crossTabAuthSync';

/**
 * Check onboarding status in Firestore using the backend user ID (NOT Firebase Auth UID).
 * Firestore security rules require using userData.id for the document path.
 */
const getOnboardingStatus = async (backendUserId: string): Promise<string | null> => {
  try {
    const userDoc = await getDoc(doc(db, 'users', backendUserId));
    if (!userDoc.exists()) return null;
    const data = userDoc.data();
    return data?.onboardingStatus || null;
  } catch (error: unknown) {
    // Callers treat `null` as "needs onboarding", so a transient Firestore
    // failure re-onboards an established user. Report it — swallowed silently
    // this was invisible in Sentry and looked like a user complaint with no trace.
    errorReporter.expected('Failed to read onboarding status from Firestore', error, {
      feature: 'onboarding',
      operation: 'getOnboardingStatus',
    });
    return null;
  }
};

const firebaseErrorMessages = {
  'auth/email-already-in-use': 'An account already exists for this email address.',
  'auth/invalid-email': 'The email address appears to be invalid.',
  'auth/operation-not-allowed': 'Email and password sign-up is currently disabled.',
  'auth/weak-password': 'Your password is too weak. Please follow the password requirements above.',
  'auth/missing-email': 'Please enter an email address to continue.',
  'auth/too-many-requests': 'Sign up has been temporarily blocked due to too many attempts. Please try again later.',
  'auth/network-request-failed': 'Network error. Please check your connection and try again.',
  'auth/timeout': 'The request timed out. Please try again.',
  'auth/user-disabled': 'This account has been disabled.',
  'auth/invalid-credential': 'Invalid authentication credentials.',
} as const;

interface SignUpFormProps {
  setShowLogin: (value: boolean) => void;
  redirectedFromSharedNote?: boolean;
  showLogin: boolean;
}

const SignUpForm: FC<SignUpFormProps> = ({ setShowLogin, redirectedFromSharedNote, showLogin }) => {
  const [searchParams] = useSearchParams();
  const noteId = searchParams.get('noteId');
  const redirectUrl = noteId ? `${RouteMap.NOTE}/${noteId}` : RouteMap.HOME;
  const navigate = useNavigate();
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const [isSigningInWithGoogle, setIsSigningInWithGoogle] = useState(false);
  const [showManualForm, setShowManualForm] = useState(false);
  const { showAlert } = useAlertStore();

  const messages = {
    required: 'This field is required',
    invalidEmail: 'Please enter a valid email address',
    invalidPhoneNumber: 'Please enter a valid phone number',
    emailsMustMatch: 'Emails must match',
  };

  const [passwordRequirements, setPasswordRequirements] = useState({
    minLength: false,
    uppercase: false,
    lowercase: false,
    specialChar: false,
    numeric: false,
  });

  const getFirebaseSignUpErrorMessage = (error: unknown) => {
    if (error instanceof FirebaseError && error.code in firebaseErrorMessages) {
      return firebaseErrorMessages[error.code as keyof typeof firebaseErrorMessages];
    }

    return 'An error occurred. Please try again.';
  };

  const checkPassword = (password: string) => {
    setPasswordRequirements({
      minLength: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      specialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
      numeric: /\d/.test(password),
    });
  };

  const {
    control,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<SignUpSchema>({
    resolver: zodResolver(getSignUpSchema(messages)),
    defaultValues: {
      email: '',
      confirmEmail: '',
      password: '',
      firstName: '',
      lastName: '',
      phoneNumber: '',
      phoneNumberCode: '+1',
    },
  });

  const onSubmit: SubmitHandler<SignUpSchema> = async (data) => {
    if (isCreatingAccount || isSigningInWithGoogle) {
      return;
    }

    // Clear logout flags BEFORE initiating signup to prevent onAuthStateChanged
    // from immediately signing out the user (fixes extension logout → login loop)
    sessionStorage.removeItem('userRequestedLogout');
    clearLogoutFlag();

    setIsCreatingAccount(true);
    const fullPhoneNumber = data.phoneNumber ? `${data.phoneNumberCode || '+1'}${data.phoneNumber}` : '';

    try {
      const res = await doCreateUserWithEmailAndPassword(
        data.email,
        data.password,
        data.firstName,
        data.lastName,
        fullPhoneNumber,
      );

      if (res.user) {
        showAlert('Account created successfully', 'success');

        // Email/password signup is always a new user — always redirect to onboarding
        const storedRedirectUrl = sessionStorage.getItem('redirectAfterLogin');
        if (storedRedirectUrl) {
          sessionStorage.setItem('postOnboardingRedirect', storedRedirectUrl);
          sessionStorage.removeItem('redirectAfterLogin');
        } else if (noteId) {
          sessionStorage.setItem('postOnboardingRedirect', redirectUrl);
        }
        navigate(RouteMap.ONBOARDING);
      }
    } catch (error) {
      showAlert(getFirebaseSignUpErrorMessage(error), 'error');
      // Sign up error handled by alert
    } finally {
      setIsCreatingAccount(false);
    }
  };

  const handleGoogleSignIn = async () => {
    if (isSigningInWithGoogle || isCreatingAccount) {
      return;
    }

    // Clear logout flags BEFORE initiating login to prevent onAuthStateChanged
    // from immediately signing out the user (fixes extension logout → login loop)
    sessionStorage.removeItem('userRequestedLogout');
    clearLogoutFlag();

    setIsSigningInWithGoogle(true);
    try {
      const { result: res, isNewUser, backendUserId } = await doSignInWithGoogle();

      if (res?.user) {
        showAlert(isNewUser ? 'Account created successfully' : 'Signed in successfully', 'success');

        const storedRedirectUrl = sessionStorage.getItem('redirectAfterLogin');

        // Determine if user needs onboarding:
        // 1. New user (isNewUser=true) → always needs onboarding
        // 2. Existing user with backendUserId → check Firestore for onboarding status
        //    (uses backendUserId = userData.id, NOT Firebase Auth UID, per Firestore security rules)
        // 3. Existing user without backendUserId (error case) → skip onboarding
        let needsOnboarding = false;

        if (isNewUser) {
          needsOnboarding = true;
        } else if (backendUserId) {
          const status = await getOnboardingStatus(backendUserId);
          needsOnboarding = !status;
        }

        if (needsOnboarding) {
          if (storedRedirectUrl) {
            sessionStorage.setItem('postOnboardingRedirect', storedRedirectUrl);
            sessionStorage.removeItem('redirectAfterLogin');
          } else if (noteId) {
            sessionStorage.setItem('postOnboardingRedirect', redirectUrl);
          }
          navigate(RouteMap.ONBOARDING);
          return;
        }

        if (storedRedirectUrl) {
          sessionStorage.removeItem('redirectAfterLogin');
          navigate(storedRedirectUrl);
          return;
        }

        const dest = noteId ? redirectUrl : RouteMap.HOME;
        navigate(dest);
      }
    } catch (error) {
      showAlert(getFirebaseSignUpErrorMessage(error), 'error');
    } finally {
      setIsSigningInWithGoogle(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FormBox>
        <TabContainer>
          <TabButton active={!showLogin} onClick={() => setShowLogin(false)}>
            Sign Up
          </TabButton>
          <TabButton active={showLogin} onClick={() => setShowLogin(true)}>
            Log In
          </TabButton>
        </TabContainer>

        <FormContent>
          <Stack>
            <Title>Create Your Account</Title>
            <GoogleButton
              variant="outlined"
              fullWidth
              size="large"
              startIcon={<GoogleIcon />}
              onClick={handleGoogleSignIn}
              disabled={isSigningInWithGoogle || isCreatingAccount}
            >
              {isSigningInWithGoogle ? 'Signing in…' : 'Sign up with Google'}
            </GoogleButton>

            <Divider sx={{ mt: 2 }}>or</Divider>

            <Button
              variant="outlined"
              onClick={() => setShowManualForm(!showManualForm)}
              sx={{
                mt: 2,
                textTransform: 'none',
                borderRadius: '100px',
                borderColor: theme.palette.grey[300],
                color: theme.palette.grey[700],
                fontWeight: 500,
                fontSize: '14px',
                padding: '8px 20px',
                '&:hover': {
                  backgroundColor: theme.palette.grey[100],
                  borderColor: theme.palette.grey[400],
                },
              }}
            >
              {showManualForm ? 'Hide manual sign up' : 'Click here to sign up manually'}
            </Button>
          </Stack>

          {redirectedFromSharedNote && (
            <Box
              sx={{
                marginTop: 2,
                marginBottom: 2,
                padding: '12px 20px',
                backgroundColor: 'rgba(33, 150, 243, 0.08)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(33, 150, 243, 0.2)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <InfoOutlined
                sx={{
                  color: theme.palette.info.main,
                  fontSize: '20px',
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.info.main,
                  fontWeight: 500,
                  fontSize: '0.875rem',
                }}
              >
                Please create an account to access this shared note.
              </Typography>
            </Box>
          )}

          {showManualForm && (
            <Stack gap={3}>
              <Stack flexDirection="row" justifyContent="space-between" gap="24px">
                <Controller
                  name="firstName"
                  control={control}
                  render={({ field }) => (
                    <LargeTextfield
                      {...field}
                      placeholder="First Name"
                      variant="outlined"
                      error={!!errors.firstName}
                      helperText={errors.firstName?.message}
                    />
                  )}
                />

                <Controller
                  name="lastName"
                  control={control}
                  render={({ field }) => (
                    <LargeTextfield
                      {...field}
                      placeholder="Last Name"
                      variant="outlined"
                      error={!!errors.lastName}
                      helperText={errors.lastName?.message}
                    />
                  )}
                />
              </Stack>

              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <LargeTextfield
                    {...field}
                    placeholder="Email Address"
                    variant="outlined"
                    fullWidth
                    error={!!errors.email}
                    helperText={errors.email?.message}
                  />
                )}
              />

              <Controller
                name="confirmEmail"
                control={control}
                render={({ field }) => (
                  <LargeTextfield
                    {...field}
                    placeholder="Confirm Email Address"
                    variant="outlined"
                    fullWidth
                    error={!!errors.confirmEmail}
                    helperText={errors.confirmEmail?.message}
                  />
                )}
              />

              <Controller
                name="phoneNumber"
                control={control}
                render={({ field }) => (
                  <PhoneNumberField
                    {...field}
                    placeholder="Phone Number (Optional)"
                    variant="outlined"
                    fullWidth
                    error={!!errors.phoneNumber}
                    helperText={errors.phoneNumber?.message}
                    onCountryChange={(code) => setValue('phoneNumberCode', code)}
                  />
                )}
              />

              <Stack>
                <Controller
                  name="password"
                  control={control}
                  render={({ field }) => (
                    <PasswordField
                      {...field}
                      onChange={(e) => {
                        field.onChange(e);
                        checkPassword(e.target.value);
                        setPasswordTouched(true);
                      }}
                      variant="outlined"
                      fullWidth
                      placeholder="Password"
                      error={!!errors.password}
                    />
                  )}
                />

                {passwordTouched && (
                  <Stack mt={1} ml={2}>
                    <Typography variant="caption" color={passwordRequirements.minLength ? 'success' : 'error'}>
                      {passwordRequirements.minLength ? '✓' : '✕'} At least 8 characters long
                    </Typography>
                    <Typography variant="caption" color={passwordRequirements.uppercase ? 'success' : 'error'}>
                      {passwordRequirements.uppercase ? '✓' : '✕'} At least one uppercase letter
                    </Typography>
                    <Typography variant="caption" color={passwordRequirements.lowercase ? 'success' : 'error'}>
                      {passwordRequirements.lowercase ? '✓' : '✕'} At least one lowercase letter
                    </Typography>
                    <Typography variant="caption" color={passwordRequirements.specialChar ? 'success' : 'error'}>
                      {passwordRequirements.specialChar ? '✓' : '✕'} At least one special character
                    </Typography>
                    <Typography variant="caption" color={passwordRequirements.numeric ? 'success' : 'error'}>
                      {passwordRequirements.numeric ? '✓' : '✕'} At least one number
                    </Typography>
                  </Stack>
                )}
              </Stack>

              <Stack flexDirection="row" alignItems="center">
                <Checkbox checked={acceptTerms} onChange={() => setAcceptTerms(!acceptTerms)} />
                <PolicyText variant="body2">
                  I agree to the <span>Terms of Service</span> and <span>Privacy Policy.</span>
                </PolicyText>
              </Stack>

              <Button
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                type="submit"
                disabled={!acceptTerms || Object.keys(errors).length > 0 || isCreatingAccount}
              >
                {isCreatingAccount ? 'Creating account…' : 'Create my account'}
              </Button>
            </Stack>
          )}
        </FormContent>
      </FormBox>
    </form>
  );
};

export default SignUpForm;
